import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { VehicleDutyCycleFigureService } from 'src/app/_services/vehicle-duty-cycle-figure.service';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components';
import { FleetDutyCycleFigureService } from 'src/app/_services/fleet-duty-cycle-figure.service';
import { CelsiusToFahrenheitPipe } from 'src/app/_shared/pipes/celsius-to-fahrenheit.pipe';
import { RangeYPipe } from 'src/app/_shared/pipes/range-y.pipe';

@Component({
  selector: 'app-temp-comfort-zone-chart-ef-chart',
  standalone: true,
  imports: [CommonModule, RbUiComponentsModule],
  templateUrl: './temp-comfort-zone-chart-ef-chart.component.html',
  styleUrls: ['./temp-comfort-zone-chart-ef-chart.component.scss']
})
export class TempComfortZoneChartEfChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles: any[] = [];
  @Input() fleetId: string[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  private readonly celsiusToFahrenheitPipe = new CelsiusToFahrenheitPipe();
  private readonly rangeYPipe = new RangeYPipe();
  deviceSerialNumber!: any[];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  allTemperatures: number[] = [];
  colorMap: { [key: string]: string } = {};

  constructor(
    private readonly vehicleDutyCycleFigureService: VehicleDutyCycleFigureService,
    private readonly fleetDutyCycleFigureService: FleetDutyCycleFigureService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.deviceSerialNumber = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    let fleetData: any[] = [];
    let vehiclesData: any[] = [];
    let fleetDataLoaded = false;
    let vehiclesDataLoaded = false;

    const updateChartIfNeeded = () => {
      if (fleetDataLoaded && vehiclesDataLoaded) {
        const combinedPlotData = [...fleetData, ...vehiclesData];
        this.updateChart(combinedPlotData);
        this.dataLoading = false;
      }
    };

    this.fleetDutyCycleFigureService.getTemperatureComfortZoneUsageFleet(this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          fleetData = this.plotTemperatureComfortZoneChart(datapoints,'fleet');
          fleetDataLoaded = true;
          updateChartIfNeeded();
        },
        error: (error) => {
          console.error('Error fetching Temperature Comfort Zone Usage Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        }
      }
    );

    this.vehicleDutyCycleFigureService.getTemperatureComfortZoneUsage(this.deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data) => {
        vehiclesData = this.plotTemperatureComfortZoneChart(data,'vehicle');
        vehiclesDataLoaded = true;
        updateChartIfNeeded();
      },
      error: (error) => {
        console.error('Error fetching Temperature Comfort Zone Usage data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    });
  }

  plotTemperatureComfortZoneChart(data: any, chartType: string): any[] {
    const dataMap: any[] = [];
    const plottedVehicles = new Set();
    this.initializeColorMap();

    Object.keys(data).flatMap((deviceSerialNumberId: string) => {
      const deviceDataArray = data[deviceSerialNumberId].timeInComfortZone.filter((item: any) => item.operationTime != 0);
      const xValues = deviceDataArray.map((dataObject: any) => this.getConvertedTemperatureValue(dataObject.batteryTemp)); // Convert to Fahrenheit
      const yValues = deviceDataArray.map((dataObject: any) => dataObject.operationTime);
      const vehicle = this.selectedVehicles.find(v => v.batteryIdentifier === deviceSerialNumberId);

      this.allTemperatures = this.allTemperatures.concat(xValues);

      const totalOpTime = yValues.reduce((acc: number, operationTime: number) => acc + operationTime, 0);
      const normalizedYValues = yValues.map((operationTime: number) => (operationTime / totalOpTime) * 100); // Normalize operation time

      for (let i = 0; i < xValues.length - 1; i++) {
        dataMap.push({
          x: [xValues[i], xValues[i + 1]],
          y: [normalizedYValues[i], normalizedYValues[i + 1]],
          type: 'scatter',
          mode: 'lines',
          name: chartType=='fleet' ? 'Fleet': vehicle.name,
          line: {color: chartType=='fleet' ? '#8A9097': this.colorMap[vehicle.batteryIdentifier]},
          showlegend: !plottedVehicles.has(chartType==='fleet' ? 'Fleet' : vehicle.batteryIdentifier)
        });
        plottedVehicles.add(chartType==='fleet' ? 'Fleet' : vehicle.batteryIdentifier);
      }
    });

    return dataMap;
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

  updateChart(data: any[]): void {
    if (this.allTemperatures.length === 0) {
      return;
    }

    const minTemp = Math.min(...this.allTemperatures);
    const maxTemp = Math.max(...this.allTemperatures);

    const allYValues = data.flatMap((trace: any) => trace.y || []);
    const [yMin, yMax] = this.rangeYPipe.transform(allYValues, 5);

    const linspace = (start: number, end: number, num: number) => {
      if (num === 1) return [start];
      const step = (end - start) / (num - 1);
      return Array.from({ length: num }, (_, i) => start + i * step);
    };

    const gradientSteps = 200;
    const xGradient = linspace(minTemp, maxTemp, gradientSteps);
    const gradientRow = linspace(0, 1, gradientSteps);
    const gradientTrace = {
      x: xGradient,
      y: [yMin, yMax],
      z: [gradientRow, gradientRow],
      type: 'heatmap',
      colorscale: [
        [0, '#56B0FF'],
        [0.33, '#FFDF95'],
        [0.66, '#5EBD82'],
        [1, '#FF8787']
      ],
      showscale: false,
      hoverinfo: 'skip',
      opacity: 0.25,
      zmin: 0,
      zmax: 1
    };

    data.unshift(gradientTrace);

    const layout = {
      title: 'Temperature comfort zone usage',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      xaxis: {
        title: this.getTemperatureAxisLabel(),
      },
      yaxis: {
        title: 'Operation time [%]',
        range: [yMin, yMax]
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
        itemclick: false,
        itemdoubleclick: false,
      },
    };

    createPlotlyChart('temp-comfort-zone-chart-ef', data, layout);
  }

  getConvertedTemperatureValue(val: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? val : this.celsiusToFahrenheitPipe.transform(val);
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? this.celsiusToFahrenheitPipe.transform(val)
        : val;
    }
  }

  getTemperatureAxisLabel(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'Battery Temperature [°C]' : 'Battery Temperature [°F]';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'Battery Temperature [°F]'
        : 'Battery Temperature [°C]';
    }
  }

  getThresholdValue(celsiusValue: number, fahrenheitValue: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? celsiusValue : fahrenheitValue;
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? fahrenheitValue
        : celsiusValue;
    }
  }
}
